// @ts-strict-ignore
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { combineLatest, from, Observable, pipe } from 'rxjs';
import { first, map, reduce, switchMap } from 'rxjs/operators';

import { OWCC_APP_VERSION } from 'src/build-env';

import { B2X_HTTP_HEADER, OWCC_HTTP_HEADER } from '../core/config/http-header.config';
import { OWCC_SESSION_STORAGE_KEY } from '../core/config/web-storage.config';
import { AbTestingService, TypedAbTestingFeature } from '../core/services/ab-testing/ab-testing.service';
import { SessionStorageService } from '../core/services/storage/session-storage/session-storage.service';
import { selectActiveLoginType } from '../store/common/common.selectors';
import { fromRegion } from '../store/mbcc/mbcc.selectors';
import { firstValueFromSync, fromRouterAsSnapshot } from '../store/router/next-router.selectors';
import { selectConfigAndTranslationRelevantParams } from '../store/runtime-variables/runtime-variables.selectors';
import { filterTruthy } from '../utils/functions/rx-filters';
import { OWCC_TICKET_PREFIX, UrlPart } from '../utils/global-constants';

const SKIP_INTERCEPTION = [
  '/config/', // carl request
  '/translations/', // carl request
  '/version', // version request
  '/markets', // doorway request
  UrlPart.Calculations, // financing request

  // testdrive validation request,
  // e.g. 'https://test.api.oneweb.mercedes-benz.com/vehicle-deeplinks-api/v1/deeplinks/DE/de/type-id/2062541_undefined-803_051?productTypes=CONFIGURATOR'
  '/vehicle-deeplinks-api/'
];

const selectPostalCodeAndActiveLoginType = createSelector(
  fromRegion.selectPostalCode,
  selectActiveLoginType,
  (postalCode, activeLoginType) => ({
    postalCode,
    activeLoginType
  })
);

const selectCarlLabel = pipe(
  selectConfigAndTranslationRelevantParams,
  map(({ carlUrl, carlLabel }) => (/^https?:\/\//.test(carlUrl) ? carlLabel : undefined))
);

const selectFormattingTag = pipe(
  selectConfigAndTranslationRelevantParams,
  map(({ formattingTag }) => formattingTag)
);

@Injectable()
export class OwccApiHeaderInjectionInterceptor implements HttpInterceptor {
  public constructor(
    private readonly abTestingService: AbTestingService,
    private readonly store: Store,
    private readonly sessionStorageService: SessionStorageService
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (SKIP_INTERCEPTION.some(part => req.url.includes(part))) {
      return next.handle(req);
    }

    const configurationDate = this.sessionStorageService.get<string>(OWCC_SESSION_STORAGE_KEY.CONFIGURATION_DATE);
    const salesLevel = this.sessionStorageService.get<string>(OWCC_SESSION_STORAGE_KEY.SALES_LEVEL_TYPE)?.toUpperCase();

    const getAbTestingRequestHeadersValues = (): Observable<string[]> =>
      this.abTestingService.abTestingInitializationBasicallyDone$.pipe(
        filterTruthy(), // wait until CARL config is loaded and at 'technically required' features are evaluated and stored in the local storage
        first(),
        switchMap(() => from(this.abTestingService.getAllEnabledBackendFeatures())),
        switchMap((feature: TypedAbTestingFeature) =>
          this.abTestingService.getUserGroupByStoryNumber(feature.storyNumber).pipe(
            first(),
            map(userGroup => `${OWCC_TICKET_PREFIX}${feature.storyNumber}:${userGroup}`)
          )
        ),
        reduce<string, string[]>((arr: string[], headerValue: string): string[] => [...arr, headerValue], [])
      );

    return combineLatest([
      this.store.select(selectPostalCodeAndActiveLoginType),
      this.store.pipe(selectCarlLabel),
      this.store.pipe(selectFormattingTag),
      getAbTestingRequestHeadersValues()
    ]).pipe(
      first(), // to close stream and not retrigger requests after e.g. postal code changes
      switchMap(([{ postalCode, activeLoginType }, carlLabel, formattingTag, abTestingHeaderValues]) => {
        let headers: Record<string, string> = {};

        for (const obj of this.buildHeaderList(
          OWCC_APP_VERSION,
          abTestingHeaderValues?.join(', '),
          carlLabel,
          formattingTag,
          configurationDate,
          salesLevel,
          postalCode,
          activeLoginType
        )) {
          if (obj.value) {
            headers = { ...headers, [obj.header]: obj.value };
          }
        }

        const clone = req.clone({
          setHeaders: headers
        });

        return next.handle(clone);
      })
    );
  }

  private buildHeaderList(
    appVersion: string,
    abTestingHeaderValues: string,
    carlLabel: string,
    formattingTag: string,
    configurationDate: string,
    salesLevel: string,
    postalCode: string,
    activeLoginType: string
  ): { value: string; header: string }[] {
    return [
      // App version
      { value: appVersion, header: OWCC_HTTP_HEADER.OWCC_VERSION },

      // AB testing
      { value: abTestingHeaderValues, header: OWCC_HTTP_HEADER.ABTEST_USERGROUP },

      /**
       * Note: There is AB testing. This is steered by configuration and the determined user group is random.
       * As a dealer I want to not get a random user group but specify them depending on the customers result.
       * Thus it's possible to override the user group by passing a query parameter (e.g. `ab=1234-b`) to OWCC.
       * In this case - and only in this case - it is required to additionally set an according header
       * to disable cache (OWCC backend requirement).
       */
      {
        value: firstValueFromSync<string>(this.store.pipe(fromRouterAsSnapshot.selectAbTestingQueryParam))
          ? 'no-cache'
          : '',
        header: 'Cache-Control'
      },

      // Carl label
      { value: carlLabel, header: OWCC_HTTP_HEADER.CARL_LABEL },

      // Formatting tag
      { value: formattingTag, header: OWCC_HTTP_HEADER.FORMATTING_TAG },

      // Configuratin Date
      { value: configurationDate, header: OWCC_HTTP_HEADER.CONFIGURATION_DATE },

      // Sale Level
      { value: salesLevel, header: OWCC_HTTP_HEADER.SALESLEVEL },

      // Zip code
      { value: postalCode, header: OWCC_HTTP_HEADER.ZIPCODE },

      // Auth State: this is (currently) only relevant when creating an online code
      // to enable vcs to differentiate between retailers and regular users
      { value: activeLoginType, header: B2X_HTTP_HEADER.AUTH_STATE }
    ];
  }
}
